class VatValidator extends HTMLElement {
  constructor() {
    super();
    this.form = this.querySelector("#ValidateVATForm");
    this.openVatBox = this.querySelector(".vat-inq-txt");
    this.handleCloseVAT = this.handleCloseVAT.bind(this);
    this.vatInput = this.querySelector("#vat_reg_no");
    this.emailInput = this.querySelector("#vat_email_address");
    this.closeVATBtn = this.querySelector("#validateVAT-close");
    this.validateVATBtn = this.querySelector("#validateVAT-btn");
    this.checkIcons = this.querySelectorAll(".VAT-wrapper .icon-check");
    this.spinnerIcons = this.querySelectorAll(".VAT-wrapper .icon-spinner");
    this.vatSuccess = this.querySelector(".vat-success");
    this.vatError = this.querySelector(".vat-error");
    this.vatNonBeBlocking = this.querySelector(".vat-non-be-blocking");
    this.extraVat = this.querySelector(".vat-extra");
    this.vatInfo = this.querySelector(".vat-info");
    this.isValidating = false;

    const messagesScript = this.querySelector("#vat-validator-messages");
    this.messages = {};
    if (messagesScript && messagesScript.textContent) {
      try {
        this.messages = JSON.parse(messagesScript.textContent);
      } catch (error) {
        console.error("Failed to parse VAT validator messages JSON", error);
      }
    }

    // Hidden fields for order attributes
    this.vatValidatedField = this.querySelector("#vat_validated");
    this.vatTimestampField = this.querySelector("#vat_validation_timestamp");
    this.vatNumberValidField = this.querySelector("#vat_no_valid");

    if (this.form) {
      this.form.addEventListener("submit", this.onFormSubmit.bind(this));
    }

    if (this.openVatBox) {
      this.openVatBox.addEventListener("click", this.handleOpenVAT.bind(this));
    }

    this.addEventListener("click", this.handleCloseVAT);

    if (this.vatInput) {
      this.vatInput.addEventListener("blur", this.onVatInputBlur.bind(this));
      this.vatInput.addEventListener("input", this.onVatInputChange.bind(this));
    }
  }

  onVatInputChange() {
    if (!this.vatInput) return;
    this.updateValidateButtonState(this.vatInput.value);
  }

  onVatInputBlur() {
    if (!this.vatInput) return;

    const vatNumber = this.vatInput.value.trim().toUpperCase();

    if (!vatNumber) {
      this.resetValidationIndicators();
      this.hideAllMessages();
      return;
    }

    const lastValidatedVat =
      this.vatNumberValidField && this.vatNumberValidField.value
        ? this.vatNumberValidField.value.toUpperCase()
        : "";
    const isValidatedMatch = lastValidatedVat && lastValidatedVat === vatNumber;

    if (!isValidatedMatch) {
      this.resetValidationIndicators();
    }

    if (vatNumber && !vatNumber.startsWith("BE")) {
      this.showNonBelgianBlocking(false);
    } else if (vatNumber && vatNumber.startsWith("BE")) {
      if (!isValidatedMatch) {
        this.hideAllMessages();
      }
    }

    this.updateValidateButtonState(vatNumber);
  }

  async onFormSubmit(event) {
    event.preventDefault();

    if (this.isValidating) return;

    if (!this.vatInput) return;

    const vatNumber = this.vatInput.value.trim().toUpperCase();
    const email =
      this.emailInput && typeof this.emailInput.value === "string"
        ? this.emailInput.value.trim()
        : "";

    // Check if VAT number is empty
    if (!vatNumber) {
      this.showApiError(
        this.getMessage("empty", "Please enter a VAT number."),
        true
      );
      return;
    }

    // Check if VAT starts with "BE" (Belgian VAT)
    if (!vatNumber.startsWith("BE")) {
      // Show blocking message and clear the field when submitting
      this.showNonBelgianBlocking(true);
      return;
    }

    if (!email) {
      this.showApiError(
        this.getMessage("emailRequired", "Email address is required."),
        true
      );
      return;
    }

    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      this.showApiError(
        this.getMessage("emailInvalid", "Please enter a valid email address."),
        true
      );
      return;
    }

    // Validate Belgian VAT using app proxy
    this.isValidating = true;
    this.setValidateButtonDisabled(true);
    try {
      await this.validateBelgianVAT(vatNumber, email);
    } finally {
      this.isValidating = false;
      this.setValidateButtonDisabled(false);
    }
  }

  async validateBelgianVAT(vatNumber, email) {
    this.createSpinner();

    try {
      const apiUrl = `/apps/dd/vat?vat_number=${encodeURIComponent(
        vatNumber
      )}&email=${encodeURIComponent(email)}`;

      const response = await fetch(apiUrl, {
        method: "GET",
        headers: {
          Accept: "application/json",
        },
      });

      if (!response.ok) {
        const message = await this.getApiErrorMessage(response);
        this.showApiError(message);
        await this.updateCartAttributes(vatNumber, false, email);
        return;
      }

      const data = await response.json();

      // If the API returned an error, surface a specific message instead of the generic
      if (data && typeof data.error === "string" && data.error) {
        let message;

        if (data.error.includes("MS_MAX_CONCURRENT_REQ")) {
          message = this.getMessage(
            "maxConcurrent",
            "The VAT validation service is currently handling too many requests. Please wait a moment and try again."
          );
        } else if (data.retryable === true) {
          message = this.getMessage(
            "serviceUnavailable",
            "The VAT validation service is temporarily unavailable. Please try again later."
          );
        } else {
          const baseMessage = this.getMessage(
            "unexpected",
            "An unexpected error occurred while validating your VAT number."
          );
          message = `${baseMessage} (${data.error})`;
        }

        this.showApiError(message, false);
        // Ensure cart attributes are cleared when validation fails due to an API error
        await this.updateCartAttributes(vatNumber, false, email);
        return;
      }

      // Check if VAT is valid
      if (data.valid === true) {
        // VAT is valid - store the data and update cart attributes
        await this.updateCartAttributes(vatNumber, true, email);
        this.showSuccess();
      } else {
        // VAT is invalid
        this.showError();
        // Clear the cart attributes for invalid VAT
        await this.updateCartAttributes(vatNumber, false, email);
      }
    } catch (error) {
      console.error("Error validating VAT number:", error);
      this.showApiError(
        this.getMessage(
          "network",
          "We could not reach the VAT validation service. Please try again."
        ),
        false
      );
    } finally {
      this.removeSpinner();
    }
  }

  async getApiErrorMessage(response) {
    let statusMessage;

    if (response.status === 429) {
      statusMessage = this.getMessage(
        "rateLimit",
        "Too many validation attempts. Please wait a moment and try again."
      );
    } else if (response.status === 422 || response.status === 400) {
      statusMessage = this.getMessage(
        "invalidFormat",
        "The VAT number format appears invalid. Please check it and try again."
      );
    } else if (response.status === 404) {
      statusMessage = this.getMessage(
        "serviceUnavailable",
        "The VAT validation service is temporarily unavailable. Please try again later."
      );
    } else {
      statusMessage = this.getMessage(
        "unexpected",
        "An unexpected error occurred while validating your VAT number."
      );
    }

    try {
      const data = await response.json();
      if (data && typeof data.error === "string" && data.error) {
        const apiError = data.error;

        if (apiError.includes("MS_MAX_CONCURRENT_REQ")) {
          return this.getMessage(
            "maxConcurrent",
            "The VAT validation service is currently handling too many requests. Please wait a moment and try again."
          );
        }

        return `${statusMessage} (${apiError})`;
      }
    } catch (error) {}

    return statusMessage;
  }

  async updateCartAttributes(vatNumber, isValid, email) {
    try {
      const timestamp = new Date().toISOString();

      const formData = new FormData();

      if (isValid) {
        // Only save the VAT number if it is valid
        formData.append("attributes[VAT number]", vatNumber);
        formData.append("attributes[VAT email]", email || "");
        formData.append("attributes[vat_validated]", "true");
        formData.append("attributes[vat_validation_timestamp]", timestamp);
      } else {
        // If invalid, clear the VAT number and timestamp from attributes
        formData.append("attributes[VAT number]", "");
        formData.append("attributes[VAT email]", "");
        formData.append("attributes[vat_validated]", "false");
        formData.append("attributes[vat_validation_timestamp]", "");
      }

      // Update cart attributes
      const response = await fetch("/cart/update.js", {
        method: "POST",
        body: formData,
      });

      if (!response.ok) {
        throw new Error("Failed to update cart attributes");
      }

      const cart = await response.json();

      // Update hidden fields
      if (this.vatValidatedField) {
        this.vatValidatedField.value = isValid ? "true" : "false";
      }
      if (isValid) {
        if (this.vatTimestampField) {
          this.vatTimestampField.value = timestamp;
        }
        if (this.vatNumberValidField) {
          this.vatNumberValidField.value = vatNumber;
        }
      } else {
        if (this.vatTimestampField) {
          this.vatTimestampField.value = "";
        }
        if (this.vatNumberValidField) {
          this.vatNumberValidField.value = "";
        }
      }

      console.log("Cart attributes updated successfully", cart.attributes);
    } catch (error) {
      console.error("Error updating cart attributes:", error);
      throw error;
    }
  }

  showSuccess() {
    this.hideAllMessages();
    if (this.vatSuccess) {
      this.vatSuccess.classList.remove("hidden");
    }
    if (this.closeVATBtn) {
      this.closeVATBtn.classList.remove("hidden");
    }
    this.checkIcons.forEach((icon) => {
      icon.classList.remove("hidden");
    });
    // Only hide vatInfo if it exists (it's commented out in the template)
    if (this.vatInfo) {
      this.vatInfo.classList.add("hidden");
    }
  }

  showError() {
    this.hideAllMessages();
    this.resetValidationIndicators();
    if (this.vatError) {
      this.vatError.classList.remove("hidden");
    }
  }

  showNonBelgianBlocking(clearField = true) {
    this.hideAllMessages();
    this.resetValidationIndicators();
    this.setValidateButtonDisabled(true);
    if (this.vatNonBeBlocking) {
      this.vatNonBeBlocking.classList.remove("hidden");
    }
    // Only clear the VAT input if requested (e.g., on form submit)
    if (clearField && this.vatInput) {
      this.vatInput.value = "";
    }
  }

  hideAllMessages() {
    if (this.vatSuccess) {
      this.vatSuccess.classList.add("hidden");
    }
    if (this.vatError) {
      this.vatError.classList.add("hidden");
    }
    if (this.vatNonBeBlocking) {
      this.vatNonBeBlocking.classList.add("hidden");
    }
    if (this.extraVat) {
      this.extraVat.classList.add("hidden");
      this.extraVat.innerHTML = "";
    }
  }

  showApiError(message, showGenericError) {
    this.hideAllMessages();
    if (showGenericError) {
      this.showError();
    }
    if (!this.extraVat) return;

    this.extraVat.textContent = message;
    this.extraVat.classList.remove("hidden");
  }

  resetValidationIndicators() {
    this.checkIcons.forEach((icon) => {
      icon.classList.add("hidden");
    });
    if (this.closeVATBtn) {
      this.closeVATBtn.classList.add("hidden");
    }
    if (this.validateVATBtn) {
      this.validateVATBtn.classList.remove("hidden");
    }
  }

  setValidateButtonDisabled(disabled) {
    if (!this.validateVATBtn) return;
    this.validateVATBtn.disabled = disabled;
  }

  updateValidateButtonState(value) {
    const vatNumber = (value || "").trim().toUpperCase();
    const shouldDisable =
      !vatNumber || !vatNumber.startsWith("BE") || this.isValidating === true;
    this.setValidateButtonDisabled(shouldDisable);
  }

  getMessage(key, fallback) {
    if (
      this.messages &&
      Object.prototype.hasOwnProperty.call(this.messages, key) &&
      typeof this.messages[key] === "string" &&
      this.messages[key]
    ) {
      return this.messages[key];
    }
    return fallback;
  }

  createSpinner() {
    this.spinnerIcons.forEach((icon) => {
      icon.classList.remove("hidden");
    });
    this.resetValidationIndicators();
    this.hideAllMessages();
  }

  removeSpinner() {
    this.spinnerIcons.forEach((icon) => {
      icon.classList.add("hidden");
    });
  }

  handleOpenVAT() {
    const wrapper = this.querySelector(".vat-validator-wrap");
    if (!wrapper) return;
    wrapper.classList.add("open");
  }

  handleCloseVAT(event) {
    const target = event && event.target;
    if (!(target instanceof Element)) return;

    const closeVATButton = target.closest(".close-VAT");
    if (closeVATButton || target.id === "validateVAT-close") {
      const wrapper = this.querySelector(".vat-validator-wrap");
      if (wrapper) {
        wrapper.classList.remove("open");
      }
      const isValidated =
        this.vatValidatedField && this.vatValidatedField.value === "true";
      if (this.vatInput && !isValidated) {
        this.vatInput.value = "";
      }
      this.hideAllMessages();
      if (this.vatSuccess) {
        this.vatSuccess.classList.add("hidden");
      }
      if (this.closeVATBtn) {
        this.closeVATBtn.classList.add("hidden");
      }
      if (this.validateVATBtn) {
        this.validateVATBtn.classList.remove("hidden");
      }
      this.checkIcons.forEach((icon) => {
        icon.classList.add("hidden");
      });
      // Only show vatInfo if it exists (it's commented out in the template)
      if (this.vatInfo) {
        this.vatInfo.classList.remove("hidden");
      }
    }
  }
}

if (!customElements.get("vat-validator")) {
  customElements.define("vat-validator", VatValidator);
}
